import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ToasterService } from '../../../services/core/toaster.service';
import { BackendApiService } from '../../../services/backend-api.service';
import { StaffService } from '../../../services/staff/staff.service';
import { CommunicationServiceService } from '../services/communication-service.service';
import { ActivityLogsCummunicationsService } from '../../shared/activity-log/activity-logs-cummunications.service';

@Component({
    selector: 'app-add-milestone-dialog',
    templateUrl: './add-milestone-dialog.component.html',
    styleUrls: ['./add-milestone-dialog.component.scss'],
    standalone: false
})
export class AddMilestoneDialogComponent implements OnInit {

  public milestoneForm!: UntypedFormGroup;
  public submitted = false;
  public isLoading = false;

  // Staff list
  public staffs: any[] = [];

  // Module data from parent
  public moduleId: number;
  public referenceId: number;
  public isEditMode: boolean = false;
  public milestoneData: any = null;
  public milestoneTasks: any[] = [];
  public dueDateError: string = '';

  constructor(
    private fb: UntypedFormBuilder,
    private toasterService: ToasterService,
    private backendApiService: BackendApiService,
    private staffService: StaffService,
    private communicationService: CommunicationServiceService,
    private activityLogService: ActivityLogsCummunicationsService,
    public dialogRef: MatDialogRef<AddMilestoneDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.moduleId = this.data?.module_id;
    this.referenceId = this.data?.reference_id;
    this.isEditMode = this.data?.isEdit || false;
    this.milestoneData = this.data?.milestone || null;
    this.milestoneTasks = this.milestoneData?.tasks || [];
  }

  ngOnInit(): void {
    this.initForm();

    // Immediately populate milestone name and date if in edit mode
    if (this.isEditMode && this.milestoneData) {
      this.milestoneForm.patchValue({
        milestone_name: this.milestoneData.milestone_name || this.milestoneData.name || '',
        due_date: this.milestoneData.due_date ? new Date(this.milestoneData.due_date) : null
      });

      // Create a temporary staff object for immediate display
      if (this.milestoneData.assigned_id && this.milestoneData.assigned_to_name) {
        const tempStaff = {
          id: this.milestoneData.assigned_id,
          full_name: this.milestoneData.assigned_to_name,
          name: this.milestoneData.assigned_to_name
        };
        this.staffs = [tempStaff];
        this.milestoneForm.patchValue({
          assigned_id: tempStaff
        });
      }
    }

    this.loadStaffMembers();
  }

  initForm(): void {
    const today = new Date();
    const defaultDueDate = new Date();
    defaultDueDate.setDate(today.getDate() + 7); // Default to 7 days from now

    this.milestoneForm = this.fb.group({
      milestone_name: ['', [Validators.required, Validators.maxLength(255)]],
      due_date: [defaultDueDate, Validators.required],
      assigned_id: [null, Validators.required]
    });

    // Add due date change listener
    this.milestoneForm.get('due_date')?.valueChanges.subscribe(selectedDate => {
      this.validateDueDate(selectedDate);
    });
  }


  loadStaffMembers(): void {
    // Store current selection if any
    const currentSelection = this.milestoneForm.value.assigned_id;

    // Load all staff members
    this.backendApiService.getApi('staff', [], false)
      .subscribe({
        next: (response: any) => {
          this.staffs = this.staffService.processStaff(response.data || response);

          // Re-set the selection with the proper staff object from the full list
          if (this.isEditMode && this.milestoneData?.assigned_id) {
            const assignedStaff = this.getStaffById(this.milestoneData.assigned_id);
            if (assignedStaff) {
              this.milestoneForm.patchValue({
                assigned_id: assignedStaff
              });
            }
          }
        },
        error: (error: any) => {
          console.error('Failed to load staff members:', error);
          // Keep the temporary staff if error occurs
          if (!currentSelection) {
            this.staffs = [];
          }
        }
      });
  }

  getStaffById(staffId: number): any {
    return this.staffs.find(staff => staff.id === staffId);
  }

  validateDueDate(selectedDate: Date | null): void {
    this.dueDateError = '';

    if (!selectedDate || !this.isEditMode) {
      return;
    }

    // Only validate if milestone has tasks
    if (!this.milestoneTasks || this.milestoneTasks.length === 0) {
      return;
    }

    const selectedTime = new Date(selectedDate).getTime();

    // Check each task's due date
    for (const task of this.milestoneTasks) {
      if (task.due_date) {
        const taskDueDate = new Date(task.due_date).getTime();

        // Only show error if milestone date is BEFORE task date (same date is allowed)
        if (selectedTime < taskDueDate) {
          const selectedDateOnly = new Date(selectedDate);
          selectedDateOnly.setHours(0, 0, 0, 0);

          const taskDateOnly = new Date(task.due_date);
          taskDateOnly.setHours(0, 0, 0, 0);

          // Compare dates without time
          if (selectedDateOnly.getTime() < taskDateOnly.getTime()) {
            const taskDateStr = new Date(task.due_date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            });

            this.dueDateError = `Milestone due date cannot be earlier than task "${task.name}" due date (${taskDateStr})`;

            // Set form control error
            this.milestoneForm.get('due_date')?.setErrors({
              'taskDateConflict': true
            });
            return;
          }
        }
      }
    }

    // Clear any existing errors if validation passes
    const currentErrors = this.milestoneForm.get('due_date')?.errors;
    if (currentErrors?.['taskDateConflict']) {
      delete currentErrors['taskDateConflict'];

      if (Object.keys(currentErrors).length === 0) {
        this.milestoneForm.get('due_date')?.setErrors(null);
      } else {
        this.milestoneForm.get('due_date')?.setErrors(currentErrors);
      }
    }
  }

  get f() {
    return this.milestoneForm.controls;
  }

  onSubmit(): void {
    this.submitted = true;

    // Validate due date if in edit mode
    if (this.isEditMode) {
      this.validateDueDate(this.milestoneForm.value.due_date);
    }

    if (this.milestoneForm.invalid || this.dueDateError) {
      if (this.dueDateError) {
        this.toasterService.showWarning(this.dueDateError);
      } else {
        this.toasterService.showWarning('Please fill all required fields.');
      }
      return;
    }

    this.isLoading = true;

    if (this.isEditMode) {
      this.updateMilestone();
    } else {
      this.createMilestone();
    }
  }

  createMilestone(): void {
    // Prepare data for API - using the milestone-data endpoint
    const formData = {
      module_id: this.moduleId,
      data_id: this.referenceId,
      milestone: this.milestoneForm.value.milestone_name,
      due_date: this.formatDate(this.milestoneForm.value.due_date),
      assigned_id: this.milestoneForm.value.assigned_id?.id || this.milestoneForm.value.assigned_id
    };

    this.backendApiService.postApi('module-data-milestones/milestone-data', formData)
      .subscribe({
        next: (response: any) => {
          this.toasterService.showSuccessMessage('Milestone created successfully!');
          // Trigger milestone refresh for all subscribers
          this.communicationService.setMilestoneRefresh(true);
          // Trigger activity log refresh
          this.activityLogService.setActivityService(true);
          this.dialogRef.close(response?.data);
        },
        error: (error: any) => {
          this.isLoading = false;
          if (error?.status !== 422) {
            this.toasterService.showError('Failed to create milestone');
          }
        },
        complete: () => {
          this.isLoading = false;
        }
      });
  }

  updateMilestone(): void {
    // Prepare data for API - updating existing milestone
    const formData = {
      milestone_name: this.milestoneForm.value.milestone_name,
      due_date: this.formatDate(this.milestoneForm.value.due_date),
      assigned_id: this.milestoneForm.value.assigned_id?.id || this.milestoneForm.value.assigned_id
    };

    const milestoneId = this.milestoneData.id;
    this.backendApiService.putApi(`module-data-milestones/${milestoneId}`, formData)
      .subscribe({
        next: (response: any) => {
          this.toasterService.showSuccessMessage('Milestone updated successfully!');
          // Trigger milestone refresh for all subscribers
          this.communicationService.setMilestoneRefresh(true);
          // Trigger activity log refresh
          this.activityLogService.setActivityService(true);
          this.dialogRef.close(response?.data);
        },
        error: (error: any) => {
          this.isLoading = false;
          if (error?.status !== 422) {
            this.toasterService.showError('Failed to update milestone');
          }
        },
        complete: () => {
          this.isLoading = false;
        }
      });
  }

  formatDate(date: Date): string {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getModuleType(moduleId: number): string {
    // Map module_id to module type based on your system
    const moduleMap: { [key: number]: string } = {
      1: 'App\\Models\\Bid',
      2: 'App\\Models\\Job',
      3: 'App\\Models\\Task',
      // Add more mappings as needed
    };
    return moduleMap[moduleId] || 'App\\Models\\Bid';
  }

  close(): void {
    this.dialogRef.close();
  }
}